import React, { useState } from 'react';
import { 
  User, Mail, MoreHorizontal, Check, X, Shield, BookOpen
} from 'lucide-react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { formatDate } from '../utils/formatUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MultiProgress, ProgressSegment } from '@/components/ui/multi-progress';
import UserProfileCard from './UserProfileCard';

export interface UserProgress {
  availableCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  notStartedCourses: number;
}

export interface UserData {
  id: string;
  name: string;
  email: string;
  role: 'student' | 'admin';
  status: 'active' | 'inactive';
  organization: string;
  avatarUrl?: string;
  createdAt: string;
  lastActive?: string;
  progress?: UserProgress;
}

interface UsersTableProps {
  users: UserData[];
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onToggleRole: (userId: string) => void;
  onToggleStatus: (userId: string) => void;
  onSort: (column: string) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  sortColumn,
  sortDirection,
  onToggleRole,
  onToggleStatus,
  onSort
}) => {
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const handleViewProfile = (user: UserData) => {
    setSelectedUser(user);
    setIsProfileOpen(true);
  };

  const handleCloseProfile = () => {
    setIsProfileOpen(false);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const getProgressSegments = (progress?: UserProgress): ProgressSegment[] => {
    if (!progress || progress.availableCourses === 0) {
      return [
        { value: 100, color: '#e5e7eb', label: 'No data' }
      ];
    }

    return [
      { 
        value: (progress.completedCourses / progress.availableCourses) * 100, 
        color: '#10b981', // green
        label: 'Completed'
      },
      { 
        value: (progress.inProgressCourses / progress.availableCourses) * 100, 
        color: '#3b82f6', // blue
        label: 'In Progress'
      },
      { 
        value: (progress.notStartedCourses / progress.availableCourses) * 100, 
        color: '#d1d5db', // gray
        label: 'Not Started'
      }
    ];
  };

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    onClick={() => onSort('name')}
                    className="flex items-center px-0 font-medium"
                  >
                    Name
                    {sortColumn === 'name' && (
                      <span className={`ml-1 ${sortDirection === 'desc' ? 'rotate-180' : ''}`}>▼</span>
                    )}
                  </Button>
                </TableHead>
                <TableHead>Organization</TableHead>
                <TableHead className="w-1/3">
                  Course Progression
                </TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    onClick={() => onSort('createdAt')}
                    className="flex items-center px-0 font-medium"
                  >
                    Joined
                    {sortColumn === 'createdAt' && (
                      <span className={`ml-1 ${sortDirection === 'desc' ? 'rotate-180' : ''}`}>▼</span>
                    )}
                  </Button>
                </TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center">
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatarUrl} alt={user.name} />
                        <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                      </Avatar>
                    </TableCell>
                    <TableCell className="font-medium">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">{user.name}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              <span>{user.email}</span>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>{user.organization}</TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <MultiProgress 
                                segments={getProgressSegments(user.progress)} 
                                className="h-3"
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-2 p-1">
                              <div className="font-semibold flex items-center gap-2">
                                <BookOpen className="h-4 w-4" />
                                <span>Course Progression</span>
                              </div>
                              <div className="text-xs space-y-1">
                                {user.progress?.availableCourses > 0 ? (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="flex items-center">
                                        <div className="w-2 h-2 bg-green-500 rounded-full mr-1" />
                                        Completed:
                                      </span>
                                      <span>{user.progress?.completedCourses || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="flex items-center">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-1" />
                                        In Progress:
                                      </span>
                                      <span>{user.progress?.inProgressCourses || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="flex items-center">
                                        <div className="w-2 h-2 bg-gray-300 rounded-full mr-1" />
                                        Not Started:
                                      </span>
                                      <span>{user.progress?.notStartedCourses || 0}</span>
                                    </div>
                                    <div className="flex justify-between pt-1 border-t">
                                      <span>Total Available:</span>
                                      <span>{user.progress?.availableCourses || 0}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div>No course data available</div>
                                )}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                        {user.role === 'admin' ? 'Admin' : 'Student'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={user.status === 'active' ? 'success' : 'outline'}
                        className={
                          user.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }
                      >
                        {user.status === 'active' ? (
                          <span className="flex items-center gap-1">
                            <Check className="h-3 w-3" /> Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <X className="h-3 w-3" /> Inactive
                          </span>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewProfile(user)}>
                            <User className="mr-2 h-4 w-4" />
                            View Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.location.href = `mailto:${user.email}`}>
                            <Mail className="mr-2 h-4 w-4" />
                            Send Email
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => onToggleRole(user.id)}>
                            <Shield className="mr-2 h-4 w-4" />
                            {user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onToggleStatus(user.id)}>
                            {user.status === 'active' ? (
                              <>
                                <X className="mr-2 h-4 w-4" />
                                Deactivate User
                              </>
                            ) : (
                              <>
                                <Check className="mr-2 h-4 w-4" />
                                Activate User
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <UserProfileCard 
        user={selectedUser} 
        isOpen={isProfileOpen} 
        onClose={handleCloseProfile}
      />
    </>
  );
};

export default UsersTable;
